import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQueries } from '@tanstack/react-query';
import { runMultiplexPeakDetection } from 'api/platform';
import { getPeakDetectionNormalizationSummary } from 'api/slideMultiplexChannelNormalizations';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { JobType, PeakDetectionJob } from 'interfaces/job';
import { MultiplexPanelsResponse, MultiplexPanelView } from 'interfaces/multiplexPanels';
import {
  every,
  filter,
  find,
  flatMap,
  includes,
  intersection,
  keys,
  map,
  pick,
  reduce,
  size,
  sortBy,
  uniq,
} from 'lodash';
import React, { useState } from 'react';
import { CasesParams } from 'utils/useCasesParams/types';
import { LoadFromPreviousJobStep } from '../../LoadFromPreviousJobStep';
import { PanelPeakDetectionEditor } from './PanelPeakDetectionEditor';
import {
  buildDefaultPeakDetectionChannelProfile,
  buildDefaultRawDogPeakDetectionChannelProfile,
  buildPanelPeakDetectionRequests,
  synchronizePeakDetectionProfiles,
} from './helpers';
import { PanelPeakDetectionConfiguration } from './types';

interface Props {
  response: MultiplexPanelsResponse;
  casesParams: CasesParams;
  selectedSlideIds: string[];
  initialUseRawDog?: boolean;
  onClose: (event?: any, force?: boolean) => void;
}

const initialConfiguration = (
  panel: MultiplexPanelView,
  slideIds: string[],
  useRawDog: boolean
): PanelPeakDetectionConfiguration => {
  const slides = filter(panel.slides, (slide) => includes(slideIds, slide.slideId));
  const channelNames = map(
    filter(
      panel.channels,
      (channel) =>
        channel.saved &&
        size(slides) &&
        every(slides, (slide) => {
          const index = find(keys(slide.channelMapping), (key) => slide.channelMapping[key] === channel.channelIndex);
          return (
            index != null &&
            includes(slide.rnaChannelIds, index) &&
            slide.channelMarkerTypes[index] === channel.saved!.analysisName
          );
        })
    ),
    (channel) => channel.saved!.analysisName
  );
  return {
    channelNames,
    useRawDog,
    defaultProfile: useRawDog
      ? buildDefaultRawDogPeakDetectionChannelProfile()
      : buildDefaultPeakDetectionChannelProfile(),
    overrides: [],
  };
};

export const PanelSpotDetectionForm = ({
  response,
  casesParams,
  selectedSlideIds,
  initialUseRawDog = true,
  onClose,
}: Props) => {
  const panels = sortBy(
    filter(
      response.panels,
      (panel) => panel.saved && size(intersection(map(panel.slides, 'slideId'), selectedSlideIds)) > 0
    ),
    (panel) => (panel.createdAt ? Date.parse(panel.createdAt) : Infinity)
  );
  const [configurations, setConfigurations] = useState<Record<string, PanelPeakDetectionConfiguration>>(() =>
    reduce(
      panels,
      (result, panel) => {
        result[panel.panelId] = initialConfiguration(panel, selectedSlideIds, initialUseRawDog);
        return result;
      },
      {} as Record<string, PanelPeakDetectionConfiguration>
    )
  );
  const [selectedPanels, setSelectedPanels] = useState(() =>
    filter(map(panels, 'panelId'), (panelId) => size(configurations[panelId].channelNames) > 0)
  );
  const [jobName, setJobName] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [normalizationName, setNormalizationName] = useState('');
  const [resolution, setResolution] = useState('');
  const [previousJobId, setPreviousJobId] = useState('');
  const [isLoadingPreviousJob, setIsLoadingPreviousJob] = useState(false);
  const [restoreWarning, setRestoreWarning] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [results, setResults] = useState<Record<string, 'started' | 'failed'>>({});
  useTrackActionFormDirty(isDirty);
  const mutation = useMutation(runMultiplexPeakDetection);
  const standardPanels = filter(
    selectedPanels,
    (panelId) => configurations[panelId] && !configurations[panelId].useRawDog && results[panelId] !== 'started'
  );
  const normalizations = useQueries({
    queries: map(standardPanels, (panelId) => {
      const panelSlideIds = intersection(map(find(panels, { panelId })?.slides || [], 'slideId'), selectedSlideIds);
      const channelNames = configurations[panelId].channelNames;
      const scope = { ...casesParams, slideIdsToInclude: panelSlideIds, slidesMode: true };
      return {
        queryKey: ['peakDetectionNormalizationSummary', scope, channelNames, normalizationName],
        queryFn: ({ signal }: { signal?: AbortSignal }) =>
          getPeakDetectionNormalizationSummary(
            { casesParams: scope, channelNames, normalizationName: normalizationName || null },
            signal
          ),
        enabled: size(panelSlideIds) > 0 && size(channelNames) > 0,
      };
    }),
  });
  let blockingMessage = restoreWarning;
  let requests: ReturnType<typeof buildPanelPeakDetectionRequests> = [];
  try {
    requests = buildPanelPeakDetectionRequests({
      panels,
      panelConfigurations: pick(
        configurations,
        filter(selectedPanels, (panelId) => results[panelId] !== 'started')
      ),
      selectedSlideIds,
      casesParams,
      jobName,
      jobDescription,
      normalizationName,
      resolutionUmpp: resolution === '' ? null : Number(resolution),
    });
    if (resolution !== '' && (!Number.isFinite(Number(resolution)) || Number(resolution) <= 0))
      blockingMessage = 'Custom resolution must be positive';
  } catch (error) {
    blockingMessage = error instanceof Error ? error.message : 'Review panel settings';
  }
  const normalizationMessages: string[] = map(standardPanels, (panelId, index) => {
    const query = normalizations[index];
    if (query.isLoading) return 'Checking selected-probe normalization';
    if (query.isError) return 'Normalization could not be loaded';
    const request = find(requests, { panelId })?.request;
    if (!request) return '';
    const missingCount = size(
      filter(request.configParams.channelNames, (marker) => {
        const summary = find(query.data || [], { markerType: marker });
        const normalizedSlideIds = uniq(flatMap(summary?.rangeGroups || [], 'slideIds'));
        return !every(request.slideIdsToInclude || [], (slideId) => includes(normalizedSlideIds, slideId));
      })
    );
    return missingCount ? `Missing normalization for ${missingCount} selected markers` : '';
  });
  if (!blockingMessage) blockingMessage = find(normalizationMessages, (message) => Boolean(message)) || '';
  const unassignedCount = size(
    filter(selectedSlideIds, (slideId) => !find(panels, (panel) => find(panel.slides, { slideId })))
  );
  const submit = async () => {
    if (blockingMessage || isLoadingPreviousJob || mutation.isLoading) return;
    const nextResults = { ...results };
    for (const entry of requests) {
      try {
        await mutation.mutateAsync(entry.request);
        nextResults[entry.panelId] = 'started';
      } catch {
        nextResults[entry.panelId] = 'failed';
      }
      setResults({ ...nextResults });
    }
    if (size(requests) && every(selectedPanels, (panelId) => nextResults[panelId] === 'started'))
      onClose(undefined, true);
  };
  const loadPreviousJob = (job?: Partial<PeakDetectionJob>) => {
    setIsDirty(true);
    setPreviousJobId(job?.id || '');
    setJobName(job?.name || '');
    setJobDescription(job?.description || '');
    if (!job) {
      const reset = reduce(
        panels,
        (result, panel) => {
          result[panel.panelId] = initialConfiguration(panel, selectedSlideIds, initialUseRawDog);
          return result;
        },
        {} as Record<string, PanelPeakDetectionConfiguration>
      );
      setConfigurations(reset);
      setSelectedPanels(filter(keys(reset), (panelId) => size(reset[panelId].channelNames) > 0));
      setRestoreWarning('');
      return;
    }
    const useRawDog = job.params?.useRawDog ?? false;
    const restored = reduce(
      panels,
      (result, panel) => {
        const available = map(panel.channels, (channel) => channel.saved?.analysisName || '');
        const channelNames = intersection(job.params?.channelNames || [], available);
        const profiles = synchronizePeakDetectionProfiles({
          selectedChannelNames: channelNames,
          profiles: job.params?.profiles,
          useRawDog,
        });
        result[panel.panelId] = {
          ...initialConfiguration(panel, selectedSlideIds, useRawDog),
          channelNames,
          overrides: map(channelNames, (channelName) => ({
            channelNames: [channelName],
            profile: profiles[channelName],
          })),
        };
        return result;
      },
      {} as Record<string, PanelPeakDetectionConfiguration>
    );
    const known = flatMap(restored, 'channelNames');
    const unknownCount = size(filter(job.params?.channelNames || [], (marker) => !includes(known, marker)));
    setRestoreWarning(unknownCount ? `${unknownCount} previous-job markers are unavailable in this panel scope` : '');
    setConfigurations(restored);
    setSelectedPanels(filter(keys(restored), (panelId) => size(restored[panelId].channelNames) > 0));
    setNormalizationName(job.params?.normalizationName || '');
    setResolution(job.params?.resolutionUmpp == null ? '' : String(job.params.resolutionUmpp));
  };
  return (
    <Stack spacing={2}>
      {!size(panels) ? (
        <Alert severity="info">Define multiplex panels for the selected slides before spot detection.</Alert>
      ) : null}
      {unassignedCount ? (
        <Alert severity="warning">{unassignedCount} selected slides have no saved panel and will not run.</Alert>
      ) : null}
      {!size(keys(results)) ? (
        <Box component="details">
          <Typography component="summary">Reuse previous job settings</Typography>
          <LoadFromPreviousJobStep
            jobType={JobType.MultiplexPeakDetection}
            selectedJobId={previousJobId}
            onSelectedJob={(job) => loadPreviousJob(job as PeakDetectionJob)}
            onSelectedJobLoadingChange={setIsLoadingPreviousJob}
          />
        </Box>
      ) : null}
      <Box component="details">
        <Typography component="summary">Job name and description</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Job name"
            value={jobName}
            onChange={(event) => {
              setIsDirty(true);
              setJobName(event.target.value);
            }}
          />
          <TextField
            label="Job description"
            value={jobDescription}
            onChange={(event) => {
              setIsDirty(true);
              setJobDescription(event.target.value);
            }}
          />
        </Stack>
      </Box>
      {map(panels, (panel) => (
        <Stack key={panel.panelId} spacing={1}>
          <PanelPeakDetectionEditor
            panel={panel}
            labId={casesParams.labId}
            customResolutionUmpp={resolution === '' ? null : Number(resolution)}
            included={includes(selectedPanels, panel.panelId)}
            selectionDisabled={mutation.isLoading || results[panel.panelId] === 'started'}
            onIncludeChange={(checked) => {
              setIsDirty(true);
              setSelectedPanels(
                checked ? [...selectedPanels, panel.panelId] : filter(selectedPanels, (id) => id !== panel.panelId)
              );
            }}
            configuration={
              configurations[panel.panelId] || initialConfiguration(panel, selectedSlideIds, initialUseRawDog)
            }
            selectedSlideIds={selectedSlideIds}
            disabled={
              mutation.isLoading || results[panel.panelId] === 'started' || !includes(selectedPanels, panel.panelId)
            }
            onChange={(configuration) => {
              setIsDirty(true);
              setConfigurations({ ...configurations, [panel.panelId]: configuration });
            }}
            normalizationStatus={
              normalizationMessages[standardPanels.indexOf(panel.panelId)] ? (
                <Alert severity="info">{normalizationMessages[standardPanels.indexOf(panel.panelId)]}</Alert>
              ) : (
                <Alert severity="success">Normalization covers every selected marker and slide.</Alert>
              )
            }
          />
          {results[panel.panelId] ? (
            <Alert severity={results[panel.panelId] === 'started' ? 'success' : 'error'}>
              {panel.name}:{' '}
              {results[panel.panelId] === 'started'
                ? 'Spot detection started'
                : 'Spot detection failed to start. Settings are retained for retry.'}
            </Alert>
          ) : null}
        </Stack>
      ))}
      <Box component="details">
        <Typography component="summary">Advanced runtime settings</Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Custom resolution (UMPP)"
            type="number"
            value={resolution}
            helperText="Optional. Default: native slide resolution."
            onChange={(event) => {
              setIsDirty(true);
              setResolution(event.target.value);
            }}
          />
          {size(standardPanels) ? (
            <TextField
              label="Normalization result name"
              value={normalizationName}
              helperText="Optional. Default: latest normalization per slide."
              onChange={(event) => {
                setIsDirty(true);
                setNormalizationName(event.target.value);
              }}
            />
          ) : null}
        </Stack>
      </Box>
      {restoreWarning ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" onClick={() => setRestoreWarning('')}>
              Discard unavailable marker settings
            </Button>
          }
        >
          {restoreWarning}
        </Alert>
      ) : null}
      {blockingMessage ? (
        <Typography role="status" variant="body2" color="text.secondary">
          {blockingMessage}
        </Typography>
      ) : null}
      <Box>
        <Button
          variant="contained"
          color="primary"
          disabled={Boolean(blockingMessage) || isLoadingPreviousJob || mutation.isLoading}
          onClick={() => void submit()}
        >
          {mutation.isLoading
            ? 'Starting spot detection'
            : size(keys(results))
            ? 'Retry failed panels'
            : 'Run spot detection'}
        </Button>
      </Box>
    </Stack>
  );
};
